import type {
  LatestWorkflowForEntityResponse,
  WorkflowEvent,
} from '@/shared/hooks/api/workflows'

export const MACHINE_CHARACTERISTICS_EXTRACTION_SECTION_KEYS = [
  'intended_use',
  'machine_form_sector',
  'permanent_access',
  'people_at_machine',
  'energy_and_drives',
  'operator_demands',
  'body_openings',
  'health_environment',
  'safeguarding',
  'control_indication',
] as const

export type MachineCharacteristicsExtractionSectionKey =
  (typeof MACHINE_CHARACTERISTICS_EXTRACTION_SECTION_KEYS)[number]

export type MachineCharacteristicsExtractionSectionStatus =
  | 'pending'
  | 'active'
  | 'done'
  | 'skipped'

export type MachineCharacteristicsExtractionTaskState = {
  sections: Record<
    MachineCharacteristicsExtractionSectionKey,
    MachineCharacteristicsExtractionSectionStatus
  >
}

export const MACHINE_CHARACTERISTICS_EXTRACTION_WORKFLOW_TYPE =
  'machine_characteristics_extraction'

function emptySectionStatuses(): Record<
  MachineCharacteristicsExtractionSectionKey,
  MachineCharacteristicsExtractionSectionStatus
> {
  return {
    intended_use: 'pending',
    machine_form_sector: 'pending',
    permanent_access: 'pending',
    people_at_machine: 'pending',
    energy_and_drives: 'pending',
    operator_demands: 'pending',
    body_openings: 'pending',
    health_environment: 'pending',
    safeguarding: 'pending',
    control_indication: 'pending',
  }
}

export function getInitialMachineCharacteristicsExtractionTaskState(): MachineCharacteristicsExtractionTaskState {
  return {
    sections: emptySectionStatuses(),
  }
}

function cloneTaskState(
  state: MachineCharacteristicsExtractionTaskState
): MachineCharacteristicsExtractionTaskState {
  return {
    sections: { ...state.sections },
  }
}

function indexOfSection(
  sectionKey: MachineCharacteristicsExtractionSectionKey
): number {
  return MACHINE_CHARACTERISTICS_EXTRACTION_SECTION_KEYS.indexOf(sectionKey)
}

function isSectionKey(value: string): value is MachineCharacteristicsExtractionSectionKey {
  return MACHINE_CHARACTERISTICS_EXTRACTION_SECTION_KEYS.includes(
    value as MachineCharacteristicsExtractionSectionKey
  )
}

function applySectionStarted(
  next: MachineCharacteristicsExtractionTaskState,
  sectionKey: MachineCharacteristicsExtractionSectionKey
): void {
  const targetIndex = indexOfSection(sectionKey)
  for (let index = 0; index < targetIndex; index += 1) {
    const id = MACHINE_CHARACTERISTICS_EXTRACTION_SECTION_KEYS[index]!
    const status = next.sections[id]
    if (status === 'pending') {
      next.sections[id] = 'done'
    }
  }
  next.sections[sectionKey] = 'active'
}

export function reduceMachineCharacteristicsExtractionTaskState(
  state: MachineCharacteristicsExtractionTaskState,
  event: WorkflowEvent
): MachineCharacteristicsExtractionTaskState {
  const next = cloneTaskState(state)

  if (event.type === 'workflow.started') {
    const workflowType =
      'workflowType' in event &&
      typeof (event as { workflowType?: string }).workflowType === 'string'
        ? (event as { workflowType: string }).workflowType
        : undefined
    if (workflowType === MACHINE_CHARACTERISTICS_EXTRACTION_WORKFLOW_TYPE) {
      return getInitialMachineCharacteristicsExtractionTaskState()
    }
    return state
  }

  if (event.type === 'workflow.completed') {
    for (const sectionKey of MACHINE_CHARACTERISTICS_EXTRACTION_SECTION_KEYS) {
      if (next.sections[sectionKey] === 'skipped') {
        continue
      }
      next.sections[sectionKey] = 'done'
    }
    return next
  }

  if (event.type === 'step.started') {
    const step = (event as { step?: string }).step
    if (step != null && isSectionKey(step)) {
      applySectionStarted(next, step)
    }
    return next
  }

  if (event.type === 'step.completed') {
    const step = (event as { step?: string }).step
    const result = (event as { result?: unknown }).result
    if (step != null && isSectionKey(step)) {
      if (
        result !== undefined &&
        result !== null &&
        typeof result === 'object' &&
        'skipped' in result &&
        (result as { skipped?: boolean }).skipped === true
      ) {
        next.sections[step] = 'skipped'
      } else {
        next.sections[step] = 'done'
      }
    }
    return next
  }

  return state
}

/** Fold persisted workflow events into section checklist state (no animation). */
export function buildMachineCharacteristicsExtractionTaskStateFromEvents(
  events: WorkflowEvent[]
): MachineCharacteristicsExtractionTaskState {
  return events.reduce(
    reduceMachineCharacteristicsExtractionTaskState,
    getInitialMachineCharacteristicsExtractionTaskState()
  )
}

export type MachineCharacteristicsExtractionRecovery = {
  isMachineCharacteristicsExtractionRunning: boolean
  isMachineCharacteristicsExtractionBegun: boolean
  machineCharacteristicsExtractionError: string | null
  machineCharacteristicsExtractionTaskState: MachineCharacteristicsExtractionTaskState
}

/** Derive onboarding UI state from a latest-workflow poll response. */
export function deriveMachineCharacteristicsExtractionRecovery(
  workflow: LatestWorkflowForEntityResponse | undefined,
  fallbackErrorMessage: string
): MachineCharacteristicsExtractionRecovery | null {
  if (workflow == null || workflow.status === 'none') {
    return null
  }

  const machineCharacteristicsExtractionTaskState =
    buildMachineCharacteristicsExtractionTaskStateFromEvents(workflow.events)

  if (workflow.status === 'running') {
    return {
      isMachineCharacteristicsExtractionRunning: true,
      isMachineCharacteristicsExtractionBegun: true,
      machineCharacteristicsExtractionError: null,
      machineCharacteristicsExtractionTaskState,
    }
  }

  if (workflow.status === 'completed') {
    return {
      isMachineCharacteristicsExtractionRunning: false,
      isMachineCharacteristicsExtractionBegun: true,
      machineCharacteristicsExtractionError: null,
      machineCharacteristicsExtractionTaskState,
    }
  }

  if (workflow.status === 'failed' || workflow.status === 'cancelled') {
    return {
      isMachineCharacteristicsExtractionRunning: false,
      isMachineCharacteristicsExtractionBegun: false,
      machineCharacteristicsExtractionError:
        workflow.error?.trim() || fallbackErrorMessage,
      machineCharacteristicsExtractionTaskState,
    }
  }

  return null
}
